import type { GameEngine, PressCapableEngine } from '@shared/gameContracts';
import type {
  GameState, SetupConfig, BetType, Press, NassauStatus,
  FullSettlement, BetStatusData, Hole18ScenariosData, HoleScore, Leg, Bet,
  RebuildRoundStateInput, RebuildRoundStateResult,
  ManualGameEvent, HoleScoreInput,
} from '@shared/gameTypes';
import {
  generateId, getPlayingOrder, getFrontHoles, getBackHoles,
  getCompletedHolesInOrder, getLastPlayedHole,
  calculateBetStatusForHoles, calculateHoleWinner,
  playerReceivesStrokeOnHole, getStrokesOnHole, calculateNetScore,
  calculateLowManHandicaps,
  convertPressesToBets, buildHoleResultMap, evaluateAllBets,
  convertToStakes, settleAll, getLegHoles, getLegEndHole, getLegStartHole,
  isRoundComplete as sharedIsRoundComplete,
  finalizeRound as sharedFinalizeRound,
  unfinalizeRound as sharedUnfinalizeRound,
} from '@/lib/gameUtils';

export function calculateNassauStatus(state: GameState): NassauStatus {
  const topTeam = state.teams[0];
  const bottomTeam = state.teams[1];
  
  const completedHoles = getCompletedHolesInOrder(state);
  if (completedHoles.length === 0) {
    return { front: 0, back: 0, overall: 0 };
  }
  
  const frontHoles = getFrontHoles(state.startingHole);
  const backHoles = getBackHoles(state.startingHole);
  
  const completedFrontHoles = completedHoles.filter(h => frontHoles.includes(h));
  const completedBackHoles = completedHoles.filter(h => backHoles.includes(h));
  
  const front = calculateBetStatusForHoles(topTeam, bottomTeam, state.scores, state.holes, completedFrontHoles);
  const back = calculateBetStatusForHoles(topTeam, bottomTeam, state.scores, state.holes, completedBackHoles);
  const overall = calculateBetStatusForHoles(topTeam, bottomTeam, state.scores, state.holes, completedHoles);
  
  return { front, back, overall };
}

export function calculatePressStatus(
  press: Press,
  state: GameState
): number {
  const topTeam = state.teams[0];
  const bottomTeam = state.teams[1];
  const completedHoles = getCompletedHolesInOrder(state);
  const playingOrder = getPlayingOrder(state.startingHole);
  
  let betHoles: number[];
  if (press.betType === 'Front') {
    betHoles = getFrontHoles(state.startingHole);
  } else if (press.betType === 'Back') {
    betHoles = getBackHoles(state.startingHole);
  } else {
    betHoles = playingOrder;
  }
  
  const pressStartPos = playingOrder.indexOf(press.startingHole);
  
  const lastBetHolePos = Math.max(...betHoles.map(h => playingOrder.indexOf(h)));
  const isLastHolePress = pressStartPos === lastBetHolePos;
  
  const pressHoles = completedHoles.filter(h => {
    const holePos = playingOrder.indexOf(h);
    if (isLastHolePress) {
      return holePos >= pressStartPos && betHoles.includes(h);
    }
    return holePos > pressStartPos && betHoles.includes(h);
  });
  
  if (pressHoles.length === 0) return 0;
  
  return calculateBetStatusForHoles(topTeam, bottomTeam, state.scores, state.holes, pressHoles);
}

export function checkForAutoPresses(state: GameState): Press[] {
  if (state.gameMode !== 'TwoDownAuto') return [];
  
  const newPresses: Press[] = [];
  const completedHoles = getCompletedHolesInOrder(state);
  const lastPlayedHole = getLastPlayedHole(state);
  
  if (completedHoles.length === 0) return [];
  
  const topTeam = state.teams[0];
  const bottomTeam = state.teams[1];
  const playingOrder = getPlayingOrder(state.startingHole);
  
  const betTypes: Array<{ type: BetType; holes: number[] }> = [
    { type: 'Front', holes: getFrontHoles(state.startingHole) },
    { type: 'Back', holes: getBackHoles(state.startingHole) },
  ];
  
  for (const bet of betTypes) {
    if (!bet.holes.includes(lastPlayedHole)) continue;
    
    const existingPressesOnThisHole = state.presses.filter(
      p => p.betType === bet.type && p.startingHole === lastPlayedHole
    );
    
    if (existingPressesOnThisHole.length > 0) continue;
    
    const completedBetHoles = completedHoles.filter(h => bet.holes.includes(h));
    const prevBetHoles = completedBetHoles.slice(0, -1);
    
    const activePresses = state.presses.filter(p => {
      if (p.betType !== bet.type) return false;
      const pressPos = playingOrder.indexOf(p.startingHole);
      const lastPos = playingOrder.indexOf(lastPlayedHole);
      return pressPos < lastPos;
    });
    
    let shouldCreatePress = false;
    let initiatedBy: 'top' | 'bottom' = 'top';
    
    if (activePresses.length === 0) {
      const currentStatus = calculateBetStatusForHoles(
        topTeam, bottomTeam, state.scores, state.holes, completedBetHoles
      );
      const prevStatus = calculateBetStatusForHoles(
        topTeam, bottomTeam, state.scores, state.holes, prevBetHoles
      );
      
      if (prevStatus > -2 && currentStatus <= -2) {
        shouldCreatePress = true;
        initiatedBy = 'top';
      } else if (prevStatus < 2 && currentStatus >= 2) {
        shouldCreatePress = true;
        initiatedBy = 'bottom';
      }
    } else {
      const latestPress = activePresses.reduce((latest, p) => {
        const latestPos = playingOrder.indexOf(latest.startingHole);
        const pPos = playingOrder.indexOf(p.startingHole);
        return pPos > latestPos ? p : latest;
      });
      
      const pressStartPos = playingOrder.indexOf(latestPress.startingHole);
      const pressHoles = completedBetHoles.filter(h => 
        playingOrder.indexOf(h) > pressStartPos
      );
      const pressPrevHoles = pressHoles.slice(0, -1);
      
      const pressCurrentStatus = calculateBetStatusForHoles(
        topTeam, bottomTeam, state.scores, state.holes, pressHoles
      );
      const pressPrevStatus = calculateBetStatusForHoles(
        topTeam, bottomTeam, state.scores, state.holes, pressPrevHoles
      );
      
      if (pressPrevStatus > -2 && pressCurrentStatus <= -2) {
        shouldCreatePress = true;
        initiatedBy = 'top';
      } else if (pressPrevStatus < 2 && pressCurrentStatus >= 2) {
        shouldCreatePress = true;
        initiatedBy = 'bottom';
      }
    }
    
    if (shouldCreatePress) {
      newPresses.push({
        id: generateId(),
        betType: bet.type,
        startingHole: lastPlayedHole,
        initiatedBy,
        stackCount: 1,
        status: 0,
        parentPressId: null,
      });
    }
  }
  
  return newPresses;
}

function calculateTurnBetsLost(state: GameState, side: 'top' | 'bottom'): number {
  const frontHoles = getFrontHoles(state.startingHole);
  const completedHoles = getCompletedHolesInOrder(state);
  
  const frontComplete = frontHoles.every(h => completedHoles.includes(h));
  if (!frontComplete) return 0;
  
  const nassau = calculateNassauStatus(state);
  
  let netBetPosition = 0;
  
  if (nassau.front !== 0) {
    const sideWinningFront = (side === 'top' && nassau.front > 0) || (side === 'bottom' && nassau.front < 0);
    netBetPosition += sideWinningFront ? 1 : -1;
  }
  
  const frontPresses = state.presses.filter(p => p.betType === 'Front');
  for (const press of frontPresses) {
    const pressStatus = calculatePressStatus(press, state);
    if (pressStatus !== 0) {
      const sideWinningPress = (side === 'top' && pressStatus > 0) || (side === 'bottom' && pressStatus < 0);
      netBetPosition += sideWinningPress ? press.stackCount : -press.stackCount;
    }
  }
  
  return netBetPosition < 0 ? Math.abs(netBetPosition) : 0;
}

function calculateHole18BetsLost(state: GameState, side: 'top' | 'bottom'): number {
  const playingOrder = getPlayingOrder(state.startingHole);
  const hole17 = playingOrder[16];
  const completedHoles = getCompletedHolesInOrder(state);
  
  if (!completedHoles.includes(hole17)) return 0;
  
  const settlement = settleGame(state);
  const totalForA = settlement.totalAmountForA;
  
  if (totalForA === 0) return 0;
  
  const isLosing = (side === 'top' && totalForA < 0) || (side === 'bottom' && totalForA > 0);
  if (!isLosing) return 0;
  
  const deficit = Math.abs(totalForA);
  
  const backHoles = getBackHoles(state.startingHole);
  const hole18 = playingOrder[17];
  const betType: BetType = backHoles.includes(hole18) ? 'Back' : 'Front';
  const pressUnit = betType === 'Back' ? state.stakes.back : state.stakes.front;
  
  if (pressUnit <= 0) return 0;
  
  const maxPresses = deficit / pressUnit;
  
  if (!Number.isInteger(maxPresses)) {
    console.error(`Hole 18 catch-up logic error: deficit ($${deficit}) / pressUnit ($${pressUnit}) = ${maxPresses} (not a whole number)`);
  }
  
  return Math.floor(maxPresses);
}

export function getMaxPressesAllowed(
  state: GameState,
  betType: BetType,
  side: 'top' | 'bottom',
  selectedHole?: number | null
): number {
  if (state.gameMode !== 'OnDemandPress') return 0;
  
  const nassau = calculateNassauStatus(state);
  const lastPlayedHole = getLastPlayedHole(state);
  const completedHoles = getCompletedHolesInOrder(state);
  
  if (completedHoles.length === 0) return 0;
  
  const frontHoles = getFrontHoles(state.startingHole);
  const backHoles = getBackHoles(state.startingHole);
  const isFrontComplete = frontHoles.every(h => completedHoles.includes(h));
  
  let status = 0;
  let inRange = false;
  
  const playingOrder = getPlayingOrder(state.startingHole);
  const lastHole = playingOrder[17];
  const secondToLastHole = playingOrder[16];
  const isHole17Complete = completedHoles.includes(secondToLastHole);
  const hole18HasScore = state.scores.some(s => s.holeNumber === lastHole && s.grossScore !== null);
  const lastHoleBetType: BetType = backHoles.includes(lastHole) ? 'Back' : 'Front';
  
  switch (betType) {
    case 'Front':
      status = nassau.front;
      inRange = frontHoles.includes(lastPlayedHole);
      
      if (lastHoleBetType === 'Front' && isHole17Complete && !hole18HasScore) {
        const isAtLastHole = lastPlayedHole === lastHole ||
                             (isHole17Complete && selectedHole === lastHole);
        if (isAtLastHole) {
          const hole18BetsLost = calculateHole18BetsLost(state, side);
          if (hole18BetsLost > 0) {
            const pressesOnLastHole = state.presses
              .filter(p => p.betType === 'Front' && p.startingHole === lastHole && p.initiatedBy === side)
              .reduce((sum, p) => sum + p.stackCount, 0);
            return Math.max(0, hole18BetsLost - pressesOnLastHole);
          }
        }
      }
      break;
    case 'Back':
      status = nassau.back;
      inRange = backHoles.includes(lastPlayedHole);
      
      const firstBackHole = backHoles[0];
      const isAtFirstBackHole = lastPlayedHole === firstBackHole || 
                                (isFrontComplete && selectedHole === firstBackHole);
      
      if (isAtFirstBackHole && isFrontComplete) {
        const turnBetsLost = calculateTurnBetsLost(state, side);
        if (turnBetsLost > 0) {
          const pressesOnFirstBackHole = state.presses
            .filter(p => p.betType === 'Back' && p.startingHole === firstBackHole && p.initiatedBy === side)
            .reduce((sum, p) => sum + p.stackCount, 0);
          
          return Math.max(0, turnBetsLost - pressesOnFirstBackHole);
        }
      }
      
      if (lastHoleBetType === 'Back' && isHole17Complete && !hole18HasScore) {
        const isAtLastHole = lastPlayedHole === lastHole ||
                             (isHole17Complete && selectedHole === lastHole);
        if (isAtLastHole) {
          const hole18BetsLost = calculateHole18BetsLost(state, side);
          if (hole18BetsLost > 0) {
            const pressesOnLastHole = state.presses
              .filter(p => p.betType === 'Back' && p.startingHole === lastHole && p.initiatedBy === side)
              .reduce((sum, p) => sum + p.stackCount, 0);
            return Math.max(0, hole18BetsLost - pressesOnLastHole);
          }
        }
      }
      break;
    case 'Overall':
      inRange = completedHoles.length > 0;
      if (!inRange) return 0;
      if (side === 'top' && state.topSideOverallPressUsed) return 0;
      if (side === 'bottom' && state.bottomSideOverallPressUsed) return 0;
      
      const isLosingMainOverall = (side === 'top' && nassau.overall < 0) || (side === 'bottom' && nassau.overall > 0);
      
      let isLosingOnOverallPress = false;
      for (const press of state.presses.filter(p => p.betType === 'Overall')) {
        const pressStatus = calculatePressStatus(press, state);
        const isLosingThisPress = (side === 'top' && pressStatus < 0) || (side === 'bottom' && pressStatus > 0);
        if (isLosingThisPress) {
          isLosingOnOverallPress = true;
          break;
        }
      }
      
      return (isLosingMainOverall || isLosingOnOverallPress) ? 1 : 0;
  }
  
  if (!inRange) return 0;
  
  const pressRights = getPressRightsForSide(state, betType, side);
  if (pressRights <= 0) return 0;
  
  const remaining = getRemainingPressesForHole(state, betType, side, lastPlayedHole);
  
  return Math.max(0, remaining);
}

export function getPressRightsForSide(
  state: GameState,
  betType: BetType,
  side: 'top' | 'bottom'
): number {
  const nassau = calculateNassauStatus(state);
  let losingBets = 0;
  
  let mainStatus = 0;
  switch (betType) {
    case 'Front': mainStatus = nassau.front; break;
    case 'Back': mainStatus = nassau.back; break;
    case 'Overall': mainStatus = nassau.overall; break;
  }
  const isLosingMain = (side === 'top' && mainStatus < 0) || (side === 'bottom' && mainStatus > 0);
  if (isLosingMain) losingBets++;
  
  for (const press of state.presses.filter(p => p.betType === betType)) {
    const pressStatus = calculatePressStatus(press, state);
    const isLosingPress = (side === 'top' && pressStatus < 0) || (side === 'bottom' && pressStatus > 0);
    if (isLosingPress) {
      losingBets += press.stackCount;
    }
  }
  
  return losingBets;
}

export function getRemainingPressesForHole(
  state: GameState,
  betType: BetType,
  side: 'top' | 'bottom',
  holeNumber: number
): number {
  const pressRights = getPressRightsForSide(state, betType, side);
  
  const pressesOnThisHole = state.presses
    .filter(p => p.betType === betType && p.startingHole === holeNumber && p.initiatedBy === side)
    .reduce((sum, p) => sum + p.stackCount, 0);
  
  return Math.max(0, pressRights - pressesOnThisHole);
}

export function canManualPress(
  state: GameState,
  betType: BetType,
  side: 'top' | 'bottom'
): boolean {
  return getMaxPressesAllowed(state, betType, side) > 0;
}

export function addManualPress(
  state: GameState,
  betType: BetType,
  side: 'top' | 'bottom',
  count: number = 1,
  selectedHole?: number
): GameState {
  const maxAllowed = getMaxPressesAllowed(state, betType, side, selectedHole);
  if (maxAllowed <= 0) return state;
  
  const actualCount = Math.min(count, maxAllowed);
  if (actualCount <= 0) return state;
  
  const lastPlayedHole = getLastPlayedHole(state);
  
  const backHoles = getBackHoles(state.startingHole);
  const frontHoles = getFrontHoles(state.startingHole);
  
  let pressStartingHole = lastPlayedHole;
  
  if (betType === 'Back' && selectedHole && backHoles.includes(selectedHole) && frontHoles.includes(lastPlayedHole)) {
    pressStartingHole = selectedHole;
  }
  
  const playingOrder = getPlayingOrder(state.startingHole);
  const lastHoleOfRound = playingOrder[17];
  if (selectedHole === lastHoleOfRound && pressStartingHole !== lastHoleOfRound) {
    const completedHoles = getCompletedHolesInOrder(state);
    const secondToLastHole = playingOrder[16];
    const isHole17Done = completedHoles.includes(secondToLastHole);
    const hole18Scored = state.scores.some(s => s.holeNumber === lastHoleOfRound && s.grossScore !== null);
    if (isHole17Done && !hole18Scored) {
      pressStartingHole = lastHoleOfRound;
    }
  }
  
  const existingPress = state.presses.find(
    p => p.betType === betType && p.startingHole === pressStartingHole && p.initiatedBy === side
  );
  
  let newPresses: Press[];
  
  if (existingPress) {
    newPresses = state.presses.map(p =>
      p.id === existingPress.id
        ? { ...p, stackCount: p.stackCount + actualCount }
        : p
    );
  } else {
    newPresses = [
      ...state.presses,
      {
        id: generateId(),
        betType,
        startingHole: pressStartingHole,
        initiatedBy: side,
        stackCount: actualCount,
        status: 0,
        parentPressId: null,
      },
    ];
  }
  
  let topSideOverallPressUsed = state.topSideOverallPressUsed;
  let bottomSideOverallPressUsed = state.bottomSideOverallPressUsed;
  
  if (betType === 'Overall') {
    if (side === 'top') topSideOverallPressUsed = true;
    if (side === 'bottom') bottomSideOverallPressUsed = true;
  }
  
  return {
    ...state,
    presses: newPresses,
    topSideOverallPressUsed,
    bottomSideOverallPressUsed,
  };
}

export function canPressExistingPress(
  state: GameState,
  pressId: string,
  side: 'top' | 'bottom'
): boolean {
  if (state.gameMode !== 'OnDemandPress') return false;
  
  const press = state.presses.find(p => p.id === pressId);
  if (!press) return false;
  
  if (press.betType === 'Overall') return false;
  
  const lastPlayedHole = getLastPlayedHole(state);
  const completedHoles = getCompletedHolesInOrder(state);
  
  if (completedHoles.length === 0) return false;
  
  let betHoles: number[];
  if (press.betType === 'Front') {
    betHoles = getFrontHoles(state.startingHole);
  } else if (press.betType === 'Back') {
    betHoles = getBackHoles(state.startingHole);
  } else {
    betHoles = getPlayingOrder(state.startingHole);
  }
  
  if (!betHoles.includes(lastPlayedHole)) return false;
  
  const completedBetHoles = completedHoles.filter(h => betHoles.includes(h));
  if (completedBetHoles.length >= betHoles.length) return false;
  
  const pressStatus = press.status;
  
  const isLosing = (side === 'top' && pressStatus < 0) || (side === 'bottom' && pressStatus > 0);
  if (!isLosing) return false;
  
  const remaining = getRemainingPressesForHole(state, press.betType, side, lastPlayedHole);
  if (remaining <= 0) return false;
  
  const existingPressOnThisPress = state.presses.find(
    p => p.parentPressId === pressId && p.startingHole === lastPlayedHole && p.initiatedBy === side
  );
  
  return !existingPressOnThisPress;
}

export function addPressOnExistingPress(
  state: GameState,
  pressId: string,
  side: 'top' | 'bottom'
): GameState {
  if (!canPressExistingPress(state, pressId, side)) return state;
  
  const parentPress = state.presses.find(p => p.id === pressId);
  if (!parentPress) return state;
  
  const lastPlayedHole = getLastPlayedHole(state);
  
  const existingPress = state.presses.find(
    p => p.parentPressId === pressId && p.startingHole === lastPlayedHole && p.initiatedBy === side
  );
  
  let newPresses: Press[];
  
  if (existingPress) {
    newPresses = state.presses.map(p =>
      p.id === existingPress.id
        ? { ...p, stackCount: p.stackCount + 1 }
        : p
    );
  } else {
    newPresses = [
      ...state.presses,
      {
        id: generateId(),
        betType: parentPress.betType,
        startingHole: lastPlayedHole,
        initiatedBy: side,
        stackCount: 1,
        status: 0,
        parentPressId: pressId,
      },
    ];
  }
  
  return {
    ...state,
    presses: newPresses,
  };
}

export function initializeGameState(config: SetupConfig): GameState {
  const playersWithStrokes = calculateLowManHandicaps(config.players, config.courseData);
  
  const team1Players = playersWithStrokes.filter((_, i) => config.players[i].teamIndex === 0);
  const team2Players = playersWithStrokes.filter((_, i) => config.players[i].teamIndex === 1);
  
  const teams: GameState['teams'] = [
    { id: generateId(), players: team1Players },
    { id: generateId(), players: team2Players },
  ];
  
  const scores: HoleScore[] = [];
  const allPlayers = [...team1Players, ...team2Players];
  
  for (const hole of config.course.holes) {
    for (const player of allPlayers) {
      scores.push({
        playerId: player.id,
        holeNumber: hole.number,
        grossScore: null,
        netScore: null,
        receivesStroke: playerReceivesStrokeOnHole(player, hole),
      });
    }
  }
  
  const startingHole = config.startingHole ?? 1;
  
  return {
    gameType: 'nassau',
    matchFormat: config.matchFormat,
    gameMode: config.gameMode,
    teams,
    holes: config.course.holes,
    scores,
    presses: [],
    nassauStatus: { front: 0, back: 0, overall: 0 },
    currentHole: startingHole,
    topSideOverallPressUsed: false,
    bottomSideOverallPressUsed: false,
    stakes: config.stakes,
    startingHole,
    isFinalized: false,
    courseDataId: config.courseData.id,
    courseSnapshot: config.courseSnapshot,
  };
}

export function updateGameStateSettings(
  state: GameState,
  config: SetupConfig
): GameState {
  const newPlayersWithStrokes = calculateLowManHandicaps(config.players, config.courseData);

  const team1New = newPlayersWithStrokes.filter((_, i) => config.players[i].teamIndex === 0);
  const team2New = newPlayersWithStrokes.filter((_, i) => config.players[i].teamIndex === 1);

  const configTeam1 = config.players.filter(p => p.teamIndex === 0);
  const configTeam2 = config.players.filter(p => p.teamIndex === 1);
  // Flatten all old players so a player who moved teams/slots can still be found by stableId.
  const allOldPlayers = [...state.teams[0].players, ...state.teams[1].players];

  const mappedTeam1 = team1New.map((p, i) => {
    const stableId = configTeam1[i]?.stableId;
    const existing = stableId
      ? allOldPlayers.find(op => op.id === stableId)
      : state.teams[0].players[i];
    return { ...p, id: existing?.id || p.id };
  });
  const mappedTeam2 = team2New.map((p, i) => {
    const stableId = configTeam2[i]?.stableId;
    const existing = stableId
      ? allOldPlayers.find(op => op.id === stableId)
      : state.teams[1].players[i];
    return { ...p, id: existing?.id || p.id };
  });

  const allNewPlayers = [...mappedTeam1, ...mappedTeam2];
  const newHoles = config.course.holes;

  const updatedScores = state.scores.map(score => {
    const player = allNewPlayers.find(p => p.id === score.playerId);
    if (!player) return score;
    const hole = newHoles.find(h => h.number === score.holeNumber);
    if (!hole) return score;
    const receivesStroke = playerReceivesStrokeOnHole(player, hole);
    const strokeCount = getStrokesOnHole(player, hole);
    const netScore = score.grossScore !== null
      ? calculateNetScore(score.grossScore, receivesStroke, strokeCount)
      : null;
    return {
      ...score,
      receivesStroke,
      netScore,
    };
  });

  const newStartingHole = config.startingHole ?? state.startingHole;

  const updatedState: GameState = {
    ...state,
    matchFormat: config.matchFormat,
    gameMode: config.gameMode,
    teams: [
      { ...state.teams[0], players: mappedTeam1 },
      { ...state.teams[1], players: mappedTeam2 },
    ],
    holes: newHoles,
    scores: updatedScores,
    stakes: config.stakes,
    startingHole: newStartingHole,
    courseDataId: config.courseData.id,
  };

  updatedState.nassauStatus = calculateNassauStatus(updatedState);

  return updatedState;
}

function removePressesForIncompleteHoles(state: GameState): Press[] {
  const completedHoles = getCompletedHolesInOrder(state);
  
  const pressIdsToRemove = new Set<string>();
  
  for (const press of state.presses) {
    if (!completedHoles.includes(press.startingHole)) {
      pressIdsToRemove.add(press.id);
    }
  }
  
  let changed = true;
  while (changed) {
    changed = false;
    for (const press of state.presses) {
      if (!pressIdsToRemove.has(press.id) && press.parentPressId && pressIdsToRemove.has(press.parentPressId)) {
        pressIdsToRemove.add(press.id);
        changed = true;
      }
    }
  }
  
  return state.presses.filter(p => !pressIdsToRemove.has(p.id));
}

function revertOverallPressUsage(
  state: GameState, 
  remainingPresses: Press[]
): { topSideOverallPressUsed: boolean; bottomSideOverallPressUsed: boolean } {
  const topOverallPresses = remainingPresses.filter(p => p.betType === 'Overall' && p.initiatedBy === 'top');
  const bottomOverallPresses = remainingPresses.filter(p => p.betType === 'Overall' && p.initiatedBy === 'bottom');
  
  return {
    topSideOverallPressUsed: topOverallPresses.length > 0,
    bottomSideOverallPressUsed: bottomOverallPresses.length > 0,
  };
}

export function updateScore(
  state: GameState,
  playerId: string,
  holeNumber: number,
  grossScore: number | null
): GameState {
  const hole = state.holes.find(h => h.number === holeNumber);
  if (!hole) return state;
  
  const player = [...state.teams[0].players, ...state.teams[1].players].find(p => p.id === playerId);
  if (!player) return state;
  
  const receivesStroke = playerReceivesStrokeOnHole(player, hole);
  const strokeCount = getStrokesOnHole(player, hole);
  const netScore = grossScore !== null ? calculateNetScore(grossScore, receivesStroke, strokeCount) : null;
  
  const updatedScores = state.scores.map(s => {
    if (s.playerId === playerId && s.holeNumber === holeNumber) {
      return { ...s, grossScore, netScore, receivesStroke };
    }
    return s;
  });
  
  let updatedState: GameState = {
    ...state,
    scores: updatedScores,
  };
  
  if (grossScore === null) {
    const cleanedPresses = removePressesForIncompleteHoles(updatedState);
    const overallFlags = revertOverallPressUsage(updatedState, cleanedPresses);
    updatedState = {
      ...updatedState,
      presses: cleanedPresses,
      topSideOverallPressUsed: overallFlags.topSideOverallPressUsed,
      bottomSideOverallPressUsed: overallFlags.bottomSideOverallPressUsed,
    };
  }
  
  updatedState.nassauStatus = calculateNassauStatus(updatedState);
  
  const autoPresses = checkForAutoPresses(updatedState);
  if (autoPresses.length > 0) {
    updatedState = {
      ...updatedState,
      presses: [...updatedState.presses, ...autoPresses],
    };
  }
  
  updatedState.presses = updatedState.presses.map(press => ({
    ...press,
    status: calculatePressStatus(press, updatedState),
  }));
  
  return updatedState;
}

export function settleGame(state: GameState): FullSettlement {
  const bets = convertPressesToBets(state);
  const holeResults = buildHoleResultMap(state);
  const evaluatedBets = evaluateAllBets(bets, holeResults, state.startingHole);
  const stakes = convertToStakes(state.stakes);
  const result = settleAll(evaluatedBets, stakes);
  return result;
}

export function getBetBreakdown(state: GameState): {
  leg: Leg;
  bets: Array<{
    pressIndex: number;
    unitCount: number;
    startHole: number;
    status: number;
    winner: 'A' | 'B' | 'Tie' | null;
  }>;
}[] {
  const bets = convertPressesToBets(state);
  const holeResults = buildHoleResultMap(state);
  const evaluatedBets = evaluateAllBets(bets, holeResults, state.startingHole);
  
  const legs: Leg[] = ['Front', 'Back', 'Overall'];
  const result: ReturnType<typeof getBetBreakdown> = [];
  
  for (const leg of legs) {
    const legBets = evaluatedBets.filter(b => b.leg === leg);
    
    const byPressIndex = new Map<number, Bet[]>();
    for (const bet of legBets) {
      const existing = byPressIndex.get(bet.pressIndex) || [];
      existing.push(bet);
      byPressIndex.set(bet.pressIndex, existing);
    }
    
    const betSummaries: {
      pressIndex: number;
      unitCount: number;
      startHole: number;
      status: number;
      winner: 'A' | 'B' | 'Tie' | null;
    }[] = [];
    
    for (const [pressIndex, units] of Array.from(byPressIndex.entries())) {
      const betHoles = getLegHoles(leg, state.startingHole);
      const playingOrder = getPlayingOrder(state.startingHole);
      const startHole = units[0].startHole;
      const startPos = playingOrder.indexOf(startHole);
      
      const lastBetHolePos = Math.max(...betHoles.map(h => playingOrder.indexOf(h)));
      const isLastHolePress = pressIndex > 0 && startPos === lastBetHolePos;
      const effectiveStartPos = pressIndex > 0 && !isLastHolePress ? startPos + 1 : startPos;
      
      let status = 0;
      for (const h of betHoles) {
        const holePos = playingOrder.indexOf(h);
        if (holePos >= effectiveStartPos) {
          const r = holeResults[h];
          if (r === 'A') status++;
          else if (r === 'B') status--;
        }
      }
      
      betSummaries.push({
        pressIndex,
        unitCount: units.length,
        startHole,
        status,
        winner: units[0].winner,
      });
    }
    
    result.push({
      leg,
      bets: betSummaries.sort((a, b) => a.pressIndex - b.pressIndex),
    });
  }
  
  return result;
}

export function computeHole18Scenarios(state: GameState): Hole18ScenariosData | null {
  if (state.gameMode !== 'OnDemandPress') return null;

  const playingOrder = getPlayingOrder(state.startingHole);
  const hole17 = playingOrder[16];
  const lastHole = playingOrder[17];
  const completedHoles = getCompletedHolesInOrder(state);

  if (!completedHoles.includes(hole17)) return null;

  const hole18HasScore = state.scores.some(s => s.holeNumber === lastHole && s.grossScore !== null);
  if (hole18HasScore) return null;

  const topTeam = state.teams[0];
  const bottomTeam = state.teams[1];

  const teamALabel = state.matchFormat === '1v1'
    ? (topTeam.players[0]?.name ?? 'Player')
    : `${topTeam.players[0]?.name ?? 'Player'} & ${topTeam.players[1]?.name || ''}`;
  const teamBLabel = state.matchFormat === '1v1'
    ? (bottomTeam.players[0]?.name ?? 'Player')
    : `${bottomTeam.players[0]?.name ?? 'Player'} & ${bottomTeam.players[1]?.name || ''}`;

  const backHoles = getBackHoles(state.startingHole);
  const lastHoleBetType: BetType = backHoles.includes(lastHole) ? 'Back' : 'Front';

  const topMax = getMaxPressesAllowed(state, lastHoleBetType, 'top', lastHole);
  const bottomMax = getMaxPressesAllowed(state, lastHoleBetType, 'bottom', lastHole);

  const topPlayerA = topTeam.players[0];
  const bottomPlayerA = bottomTeam.players[0];
  const holePar = state.holes.find(h => h.number === lastHole)?.par ?? 4;

  function buildSimState(outcome: 'tie' | 'A' | 'B'): GameState {
    const filteredScores = state.scores.filter(s => s.holeNumber !== lastHole);

    const topGross = holePar;
    const bottomGross = holePar;
    const topStroke = state.holes.find(h => h.number === lastHole)
      ? topPlayerA.strokesReceived > 0 && state.holes.find(h => h.number === lastHole)!.strokeIndex <= topPlayerA.strokesReceived
      : false;
    const bottomStroke = state.holes.find(h => h.number === lastHole)
      ? bottomPlayerA.strokesReceived > 0 && state.holes.find(h => h.number === lastHole)!.strokeIndex <= bottomPlayerA.strokesReceived
      : false;
    const topNet = topGross - (topStroke ? 1 : 0);
    const bottomNet = bottomGross - (bottomStroke ? 1 : 0);

    let syntheticScores: HoleScore[];

    if (outcome === 'tie') {
      syntheticScores = [
        { playerId: topPlayerA.id, holeNumber: lastHole, grossScore: topGross, netScore: topNet, receivesStroke: !!topStroke },
        { playerId: bottomPlayerA.id, holeNumber: lastHole, grossScore: bottomGross, netScore: bottomNet, receivesStroke: !!bottomStroke },
      ];
      if (topNet !== bottomNet) {
        syntheticScores[1] = { ...syntheticScores[1], grossScore: topGross + (bottomStroke ? 1 : 0) - (topStroke ? 1 : 0), netScore: topNet };
      }
    } else if (outcome === 'A') {
      syntheticScores = [
        { playerId: topPlayerA.id, holeNumber: lastHole, grossScore: topGross, netScore: topNet, receivesStroke: !!topStroke },
        { playerId: bottomPlayerA.id, holeNumber: lastHole, grossScore: bottomGross + 1, netScore: topNet + 1, receivesStroke: !!bottomStroke },
      ];
    } else {
      syntheticScores = [
        { playerId: topPlayerA.id, holeNumber: lastHole, grossScore: topGross + 1, netScore: bottomNet + 1, receivesStroke: !!topStroke },
        { playerId: bottomPlayerA.id, holeNumber: lastHole, grossScore: bottomGross, netScore: bottomNet, receivesStroke: !!bottomStroke },
      ];
    }

    if (state.matchFormat === '2v2') {
      const topPlayerB = topTeam.players[1];
      const bottomPlayerB = bottomTeam.players[1];
      if (topPlayerB) {
        syntheticScores.push({ playerId: topPlayerB.id, holeNumber: lastHole, grossScore: topGross + 2, netScore: topNet + 2, receivesStroke: false });
      }
      if (bottomPlayerB) {
        syntheticScores.push({ playerId: bottomPlayerB.id, holeNumber: lastHole, grossScore: bottomGross + 2, netScore: bottomNet + 2, receivesStroke: false });
      }
    }

    return {
      ...state,
      scores: [...filteredScores, ...syntheticScores],
    };
  }

  function countPressNetForA(simState: GameState): number {
    const bets = convertPressesToBets(simState);
    const holeResults = buildHoleResultMap(simState);
    const evalBets = evaluateAllBets(bets, holeResults, simState.startingHole);
    let pressNetA = 0;
    for (const bet of evalBets) {
      if (bet.pressIndex === 0) continue;
      if (bet.winner === 'A') pressNetA++;
      else if (bet.winner === 'B') pressNetA--;
    }
    return pressNetA;
  }

  const scenarios: Hole18ScenariosData['scenarios'] = [];

  for (const outcome of ['A', 'B'] as const) {
    const simState = buildSimState(outcome);
    const settlement = settleGame(simState);
    const pressNetA = countPressNetForA(simState);

    const label = outcome === 'A'
      ? `If ${teamALabel} Wins Hole ${lastHole}`
      : `If ${teamBLabel} Wins Hole ${lastHole}`;

    scenarios.push({
      label,
      teamANet: Math.round(settlement.totalAmountForA * 100) / 100,
      teamBNet: Math.round(settlement.totalAmountForB * 100) / 100,
      teamAPressNet: pressNetA,
      teamBPressNet: -pressNetA,
    });
  }

  return {
    scenarios,
    teamACanPress: topMax > 0,
    teamBCanPress: bottomMax > 0,
    teamAMaxPresses: topMax,
    teamBMaxPresses: bottomMax,
    teamALabel,
    teamBLabel,
    lastHole,
  };
}

export function computeBetStatus(state: GameState): BetStatusData {
  const completedHoles = getCompletedHolesInOrder(state);
  const holesCompleted = completedHoles.length;
  const lastCompletedHole = holesCompleted > 0 ? completedHoles[holesCompleted - 1] : null;

  const teamALabel = state.teams[0].players.length === 1
    ? (state.teams[0].players[0]?.name ?? 'Player')
    : `${state.teams[0].players[0]?.name || ''}/${state.teams[0].players[1]?.name || ''}`;
  const teamBLabel = state.teams[1].players.length === 1
    ? (state.teams[1].players[0]?.name ?? 'Player')
    : `${state.teams[1].players[0]?.name || ''}/${state.teams[1].players[1]?.name || ''}`;

  if (holesCompleted === 0) {
    return {
      lastCompletedHole: null,
      holesCompleted: 0,
      closedBets: [],
      openBetsByLeg: [],
      openNetSummary: { netUnitsForA: 0, netDollarsForA: 0, totalActiveBets: 0 },
      projectedTotalA: { units: 0, dollars: 0 },
      projectedTotalB: { units: 0, dollars: 0 },
      teamALabel,
      teamBLabel,
    };
  }

  const bets = convertPressesToBets(state);
  const holeResults = buildHoleResultMap(state);
  const evaluatedBets = evaluateAllBets(bets, holeResults, state.startingHole);
  const stakes = convertToStakes(state.stakes);
  const playingOrder = getPlayingOrder(state.startingHole);
  const frontHoles = getFrontHoles(state.startingHole);
  const backHoles = getBackHoles(state.startingHole);

  const isBetClosed = (bet: Bet): boolean => {
    const betHoles = getLegHoles(bet.leg, state.startingHole);
    const betStartPos = playingOrder.indexOf(bet.startHole);
    const betEndPos = playingOrder.indexOf(bet.endHole);
    const isPress = bet.pressIndex > 0;
    const isLastHolePress = isPress && betStartPos === betEndPos;
    const effectiveStartPos = isPress && !isLastHolePress ? betStartPos + 1 : betStartPos;

    let allCompleted = true;
    let currentStanding = 0;
    let remainingHoles = 0;

    for (const h of betHoles) {
      const pos = playingOrder.indexOf(h);
      if (pos >= effectiveStartPos && pos <= betEndPos) {
        if (completedHoles.includes(h)) {
          const r = holeResults[h];
          if (r === 'A') currentStanding++;
          else if (r === 'B') currentStanding--;
        } else {
          allCompleted = false;
          remainingHoles++;
        }
      }
    }

    if (allCompleted) return true;

    if (Math.abs(currentStanding) > remainingHoles) return true;

    return false;
  };

  const closedBets: Bet[] = [];
  const openBets: Bet[] = [];
  for (const bet of evaluatedBets) {
    if (isBetClosed(bet)) {
      closedBets.push(bet);
    } else {
      openBets.push(bet);
    }
  }

  const closedSummaries: BetStatusData['closedBets'] = [];
  const legs: Leg[] = ['Front', 'Back', 'Overall'];
  for (const leg of legs) {
    const legClosed = closedBets.filter(b => b.leg === leg);
    if (legClosed.length === 0) continue;
    let netUnitsForA = 0;
    for (const b of legClosed) {
      if (b.winner === 'A') netUnitsForA += 1;
      else if (b.winner === 'B') netUnitsForA -= 1;
    }
    const legLabel = leg === 'Front'
      ? (frontHoles.every(h => completedHoles.includes(h)) ? 'Front Nine' : 'Front (Closed Bets)')
      : leg === 'Back'
      ? (backHoles.every(h => completedHoles.includes(h)) ? 'Back Nine' : 'Back (Closed Bets)')
      : 'Overall';
    closedSummaries.push({
      leg,
      label: legLabel,
      netUnitsForA,
      netDollars: netUnitsForA * stakes[leg],
      betCount: legClosed.length,
    });
  }

  const getBetCurrentStanding = (bet: Bet): number => {
    const betHoles = getLegHoles(bet.leg, state.startingHole);
    const betStartPos = playingOrder.indexOf(bet.startHole);
    const betEndPos = playingOrder.indexOf(bet.endHole);
    const isPress = bet.pressIndex > 0;
    const isLastHolePress = isPress && betStartPos === betEndPos;
    const effectiveStartPos = isPress && !isLastHolePress ? betStartPos + 1 : betStartPos;

    let aUp = 0;
    for (const h of betHoles) {
      const pos = playingOrder.indexOf(h);
      if (pos >= effectiveStartPos && pos <= betEndPos && completedHoles.includes(h)) {
        const r = holeResults[h];
        if (r === 'A') aUp++;
        else if (r === 'B') aUp--;
      }
    }
    return aUp;
  };

  const hasBetStarted = (bet: Bet): boolean => {
    const betHoles = getLegHoles(bet.leg, state.startingHole);
    const betStartPos = playingOrder.indexOf(bet.startHole);
    const betEndPos = playingOrder.indexOf(bet.endHole);
    for (const h of betHoles) {
      const pos = playingOrder.indexOf(h);
      if (pos >= betStartPos && pos <= betEndPos && completedHoles.includes(h)) {
        return true;
      }
    }
    return false;
  };

  const openBetsByLeg: BetStatusData['openBetsByLeg'] = [];
  for (const leg of legs) {
    const legOpenBets = openBets.filter(b => b.leg === leg && hasBetStarted(b));
    if (legOpenBets.length === 0) continue;

    const standingCounts = new Map<number, number>();
    for (const bet of legOpenBets) {
      const standing = getBetCurrentStanding(bet);
      standingCounts.set(standing, (standingCounts.get(standing) || 0) + 1);
    }

    const groups = Array.from(standingCounts.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([standing, count]) => ({ standing, count }));

    openBetsByLeg.push({ leg, groups, totalBets: legOpenBets.length });
  }

  let projUnitsA = 0;
  for (const b of closedBets) {
    if (b.winner === 'A') projUnitsA += 1;
    else if (b.winner === 'B') projUnitsA -= 1;
  }
  let projDollarsA = 0;
  for (const leg of legs) {
    const legClosedBets = closedBets.filter(b => b.leg === leg);
    let legUnits = 0;
    for (const b of legClosedBets) {
      if (b.winner === 'A') legUnits += 1;
      else if (b.winner === 'B') legUnits -= 1;
    }
    projDollarsA += legUnits * stakes[leg];
  }

  for (const bet of openBets) {
    if (!hasBetStarted(bet)) continue;
    const standing = getBetCurrentStanding(bet);
    projUnitsA += standing > 0 ? 1 : standing < 0 ? -1 : 0;
    const legStake = stakes[bet.leg];
    projDollarsA += standing > 0 ? legStake : standing < 0 ? -legStake : 0;
  }

  let openNetUnitsA = 0;
  let openNetDollarsA = 0;
  let totalActiveBets = 0;
  for (const bet of openBets) {
    if (!hasBetStarted(bet)) continue;
    totalActiveBets++;
    const standing = getBetCurrentStanding(bet);
    const legStake = stakes[bet.leg];
    if (standing > 0) { openNetUnitsA += 1; openNetDollarsA += legStake; }
    else if (standing < 0) { openNetUnitsA -= 1; openNetDollarsA -= legStake; }
  }

  return {
    lastCompletedHole,
    holesCompleted,
    closedBets: closedSummaries,
    openBetsByLeg,
    openNetSummary: { netUnitsForA: openNetUnitsA, netDollarsForA: openNetDollarsA, totalActiveBets },
    projectedTotalA: { units: projUnitsA, dollars: projDollarsA },
    projectedTotalB: { units: -projUnitsA, dollars: -projDollarsA },
    teamALabel,
    teamBLabel,
  };
}

function getMatchStatus(state: GameState) {
  return calculateNassauStatus(state);
}

function getPressStatuses(state: GameState): Record<string, number> {
  const result: Record<string, number> = {};
  for (const press of state.presses) {
    result[press.id] = calculatePressStatus(press, state);
  }
  return result;
}

export function rebuildRoundState(input: RebuildRoundStateInput): RebuildRoundStateResult {
  const { roundConfig, scores: scoreInputs, manualGameEvents } = input;

  const allPlayers = roundConfig.teams.flatMap(t => t.players);
  const playingOrder = getPlayingOrder(roundConfig.startingHole);

  const sortedScores = [...scoreInputs].sort(
    (a, b) => playingOrder.indexOf(a.holeNumber) - playingOrder.indexOf(b.holeNumber)
  );

  const activeEvents = manualGameEvents
    .filter(e => !e.deleted)
    .sort((a, b) => {
      const posA = playingOrder.indexOf(a.startingHole);
      const posB = playingOrder.indexOf(b.startingHole);
      return posA - posB;
    });

  const fullScores: HoleScore[] = [];
  for (const hole of roundConfig.holes) {
    for (const player of allPlayers) {
      fullScores.push({
        playerId: player.id,
        holeNumber: hole.number,
        grossScore: null,
        netScore: null,
        receivesStroke: playerReceivesStrokeOnHole(player, hole),
      });
    }
  }

  let state: GameState = {
    gameType: roundConfig.gameType,
    matchFormat: roundConfig.matchFormat,
    gameMode: roundConfig.gameMode,
    teams: roundConfig.teams,
    holes: roundConfig.holes,
    scores: fullScores,
    presses: [],
    nassauStatus: { front: 0, back: 0, overall: 0 },
    currentHole: roundConfig.startingHole,
    topSideOverallPressUsed: false,
    bottomSideOverallPressUsed: false,
    stakes: roundConfig.stakes,
    startingHole: roundConfig.startingHole,
    isFinalized: roundConfig.isFinalized ?? false,
    courseDataId: roundConfig.courseDataId,
    roundId: roundConfig.roundId,
  };

  let activeHole: number | null = null;

  for (const holeNumber of playingOrder) {
    const holeScores = sortedScores.filter(s => s.holeNumber === holeNumber);

    for (const si of holeScores) {
      if (si.grossScore === null) continue;

      const hole = roundConfig.holes.find(h => h.number === holeNumber);
      if (!hole) continue;
      const player = allPlayers.find(p => p.id === si.playerId);
      if (!player) continue;

      const receivesStroke = playerReceivesStrokeOnHole(player, hole);
      const strokeCount = getStrokesOnHole(player, hole);
      const netScore = (si.netScore != null) ? si.netScore : calculateNetScore(si.grossScore, receivesStroke, strokeCount);

      state = {
        ...state,
        scores: state.scores.map(s => {
          if (s.playerId === si.playerId && s.holeNumber === holeNumber) {
            return { ...s, grossScore: si.grossScore, netScore, receivesStroke };
          }
          return s;
        }),
      };
    }

    const holeComplete = allPlayers.every(p => {
      const s = state.scores.find(sc => sc.playerId === p.id && sc.holeNumber === holeNumber);
      return s && s.grossScore !== null;
    });

    if (!holeComplete) {
      if (activeHole === null) {
        activeHole = holeNumber;
      }
      continue;
    }

    state.currentHole = holeNumber;

    const eventsForHole = activeEvents.filter(
      e => e.type === 'press' && e.startingHole === holeNumber
    );
    for (const evt of eventsForHole) {
      const alreadyExists = state.presses.some(p => p.id === evt.id);
      if (alreadyExists) continue;

      const press: Press = {
        id: evt.id,
        betType: evt.betType,
        startingHole: evt.startingHole,
        initiatedBy: evt.initiatedBy,
        stackCount: evt.stackCount,
        status: 0,
        parentPressId: evt.parentPressId,
      };
      state = {
        ...state,
        presses: [...state.presses, press],
      };

      if (evt.betType === 'Overall') {
        if (evt.initiatedBy === 'top') {
          state = { ...state, topSideOverallPressUsed: true };
        } else {
          state = { ...state, bottomSideOverallPressUsed: true };
        }
      }
    }

    state.nassauStatus = calculateNassauStatus(state);

    const autoPresses = checkForAutoPresses(state);
    if (autoPresses.length > 0) {
      state = {
        ...state,
        presses: [...state.presses, ...autoPresses],
      };
    }

    state = {
      ...state,
      presses: state.presses.map(press => ({
        ...press,
        status: calculatePressStatus(press, state),
      })),
    };
  }

  if (activeHole !== null && roundConfig.gameMode !== 'TwoDownAuto') {
    const midHoleEvents = activeEvents.filter(
      e => e.type === 'press' && e.startingHole === activeHole
    );
    for (const evt of midHoleEvents) {
      const alreadyExists = state.presses.some(p => p.id === evt.id);
      if (alreadyExists) continue;

      const press: Press = {
        id: evt.id,
        betType: evt.betType,
        startingHole: evt.startingHole,
        initiatedBy: evt.initiatedBy,
        stackCount: evt.stackCount,
        status: 0,
        parentPressId: evt.parentPressId,
      };
      state = {
        ...state,
        presses: [...state.presses, press],
      };

      if (evt.betType === 'Overall') {
        if (evt.initiatedBy === 'top') {
          state = { ...state, topSideOverallPressUsed: true };
        } else {
          state = { ...state, bottomSideOverallPressUsed: true };
        }
      }
    }

    if (midHoleEvents.length > 0) {
      state.nassauStatus = calculateNassauStatus(state);

      state = {
        ...state,
        presses: state.presses.map(press => ({
          ...press,
          status: calculatePressStatus(press, state),
        })),
      };
    }
  }

  if (activeHole === null && sharedIsRoundComplete(state)) {
    activeHole = null;
  } else if (activeHole === null) {
    activeHole = playingOrder[0];
  }

  return {
    gameState: state,
    activeHole,
  };
}

export const nassauEngine: GameEngine & PressCapableEngine = {
  gameType: 'nassau',
  settleGame,
  calculateBetStatus: computeBetStatus,
  getMatchStatus,
  initializeGameState,
  updateScore,
  updateGameStateSettings,
  isRoundComplete: sharedIsRoundComplete,
  finalizeRound: sharedFinalizeRound,
  unfinalizeRound: sharedUnfinalizeRound,
  rebuildRoundState,
  getPressStatuses,
  checkForAutoPresses,
  getMaxPressesAllowed,
  computeHole18Scenarios,
  addManualPress,
  addPressOnExistingPress,
  canManualPress,
  canPressExistingPress,
  getPressRightsForSide,
  getRemainingPressesForHole,
};
